import { HookModule, ModuleManager, ViewModule } from "@module-system";

import locales from "./locales";
import { rawBgBlurState } from "./blurState";
import bgBlur from "./blurModule";
import { EventManager, Events } from "@events";
import BackgroundSettings from "./views/BackgroundSettings.vue";
import CommonConditions from "@lib/commonConditions";
import BackgroundSettingsTab from "./views/BackgroundSettingsTab.vue";
import AgentPreviewBackgroundToggle from "./views/AgentPreviewBackgroundToggle.vue";
import { getTFLiteModelFileName } from "./helpers/segmentationHelper";
import { useVideoCallState } from "@/stores/videoCallState";
import { useLicenseState } from "@/stores/licenseState";
import { useDeviceState } from "@/stores/deviceState";

/**
 * @type {import('@module-system').IModule}
 */
var module = {
  name: "BgBlurReplacement",
  version: "1.0.0",
  bgBlur,
  rawBgBlurState,
  getBgBlurParams() {
    return {
      sigmaColor: rawBgBlurState.sigmaColor,
      sigmaSpace: rawBgBlurState.sigmaSpace,
      blendMode: rawBgBlurState.blendMode,
      coverageMin: rawBgBlurState.coverageMin,
      coverageMax: rawBgBlurState.coverageMax,
      lightWrapping: rawBgBlurState.lightWrapping,
      smoothSegmentationMask: rawBgBlurState.smoothSegmentationMask,
      pipeline: rawBgBlurState.backgroundPipeline,
      blurAmount: rawBgBlurState.blurAmount,
      backgroundSegmentationModel: rawBgBlurState.backgroundSegmentationModel,
      scale: rawBgBlurState.scale,
      backgroundOperation: rawBgBlurState.backgroundOperation,
      backgroundUrl: rawBgBlurState.backgroundUrl,
      quality: rawBgBlurState.quality,
      backgroundActive: rawBgBlurState.backgroundActive,
      backgroundInferenceWorker: rawBgBlurState.backgroundInferenceWorker,
    };
  },
  async register() {
    const licenseState = useLicenseState();

    if (!licenseState.hasLicensePackage("VirtualBackground")) {
      console.warn("VirtualBackground license not found");
      return;
    }

    ModuleManager.addLocales(locales, "modifyUserMedia");

    function applyBgSettings(settings: any) {
      rawBgBlurState.backgroundActive.value = settings.backgroundActive ?? rawBgBlurState.backgroundActive.value;
      console.log("backgroundActive", rawBgBlurState.backgroundActive.value);
      rawBgBlurState.backgroundOperation.value = settings.backgroundOperation ?? rawBgBlurState.backgroundOperation.value;
      rawBgBlurState.backgroundPipeline.value = settings.backgroundPipeline ?? rawBgBlurState.backgroundPipeline.value;
      rawBgBlurState.backgroundSegmentationModel.value =
        settings.backgroundSegmentationModel ?? rawBgBlurState.backgroundSegmentationModel.value;
      rawBgBlurState.backgroundUrl.value = settings.backgroundUrl ?? rawBgBlurState.backgroundUrl.value;
      rawBgBlurState.blurAmount.value = settings.blurAmount ?? rawBgBlurState.blurAmount;
      // Missing key => stays at the default (true). Explicit false disables the worker for this scope.
      rawBgBlurState.backgroundInferenceWorker.value =
        settings.backgroundInferenceWorker ?? rawBgBlurState.backgroundInferenceWorker.value;
    }

    function applyCustomBgSettings(rtcId) {
      const state = useVideoCallState(rtcId);
      if (state && state.callData) {
        // check queue settings
        if (state.callData?.queue?.settings?.backgroundSettings) {
          const queueBgSettings = JSON.parse(state.callData.queue.settings.backgroundSettings);
          console.log("Background Module queueBgSettings", queueBgSettings);
          applyBgSettings(queueBgSettings);
        }

        // check agent settings
        if (state.callData?.agent?.settings?.backgroundSettings) {
          const agentBgSettings = JSON.parse(state.callData.agent.settings.backgroundSettings);
          console.log("Background Module agentBgSettings", agentBgSettings);
          applyBgSettings(agentBgSettings);
        }
      }
    }

    EventManager.rootEventEmitter().on("config-overrides-loaded", async () => {
      // @ts-ignore
      const defaultBgSettings = JSON.parse(window.CONFIG["BG_DEFAULT_SETTINGS"] || "null");

      if (defaultBgSettings) {
        console.log("Background Module defaultBgSettings", defaultBgSettings);
        applyBgSettings(defaultBgSettings);
      }
    });

    ModuleManager.addViewModule(
      new ViewModule(
        "CameraSettingsTabs",
        "background-settings",
        BackgroundSettingsTab,
        0,
        () => CommonConditions.isAgent(undefined),
        {},
        true,
      ),
    );

    ModuleManager.addViewModule(
      new ViewModule(
        "CameraSettingsWindows",
        "background-settings",
        BackgroundSettings,
        0,
        () => CommonConditions.isAgent(undefined),
        {},
        true,
      ),
    );

    // Preview opt-in checkbox — injected into the agent-preview page. Present only because this module
    // registered it, so it disappears automatically when the module is absent/unlicensed.
    ModuleManager.addViewModule(
      new ViewModule(
        "AgentPreviewControls",
        "background-preview-toggle",
        AgentPreviewBackgroundToggle,
        0,
        () => CommonConditions.isAgent(undefined),
        {},
        true,
      ),
    );

    // Warm the browser cache with everything background replacement needs, so the first real call doesn't
    // pay the model/wasm download. Triggered from the agent-preview page, regardless of the checkbox state.
    ModuleManager.addHookModule(
      new HookModule({
        critical: false,
        name: "BgBlurReplacement preload",
        description: "Prefetches segmentation model + wasm assets to warm the HTTP cache",
        location: "agentPreviewPreload",
        priority: 0,
        run: (state) => {
          const urls = ["/tflite/tflite-simd.js", "/tflite/tflite-simd.wasm", "/tflite/tflite.js", "/tflite/tflite.wasm"];

          const model = rawBgBlurState.backgroundSegmentationModel.value;
          // Both resolutions for the current model cover either quality setting without duplicating the map.
          for (const res of ["256x144", "256x256"] as const) {
            try {
              urls.push(`/models/${getTFLiteModelFileName(model, res)}.tflite`);
            } catch {
              // model has no file for this resolution; skip
            }
          }

          if ((window as any).CONFIG?.BG_SEGMENTATION_BACKEND === "mediapipe") {
            urls.push(
              "/mediapipe/wasm/vision_wasm_internal.js",
              "/mediapipe/wasm/vision_wasm_internal.wasm",
              "/models/selfie_segmenter.tflite",
            );
          }

          const unique = [...new Set(urls)];
          console.log("BgBlur: preloading background assets to warm cache", unique);
          unique.forEach((u) => fetch(u).catch(() => {}));
          return state;
        },
        condition: CommonConditions.isAgent,
      }),
    );

    // rtcIds for which the CallDataReceived/VideoCallEnded listeners are already wired (register once each).
    const bgRegisteredRtcIds = new Set<string>();
    // Blur pipeline per room, so tearing one down on reacquire/call-end never touches another room's.
    // Keyed by rtcId ("preview" for the no-call preview page). The agent can hold several at once — main
    // call + a supervisor whisper room in the same tab — so this MUST NOT be a single global.
    const currentBlurByRoom = new Map<string, { cleanUp: () => void }>();

    ModuleManager.addHookModule(
      new HookModule({
        critical: false,
        name: "BgBlurReplacement modifyUserMedia",
        description: "Blurs the background of the user media or replaces it with an image",
        location: "modifyUserMedia",
        priority: 0,

        run: async ({ media: mediaOrj, rtcId, resetMedia }) => {
          // No rtcId => not a call (e.g. the agent-preview page, which only keeps the camera warm).
          const isPreview = !rtcId;
          const roomKey = isPreview ? "preview" : rtcId;

          if (!isPreview) {
            applyCustomBgSettings(rtcId);

            // Wire per-call listeners only once. This hook re-runs on every getUserMedia (device change,
            // resetMedia); re-adding the CallDataReceived listener each time leaked listeners and turned
            // one CallDataReceived into a cascade of camera reacquisitions.
            if (!bgRegisteredRtcIds.has(rtcId)) {
              bgRegisteredRtcIds.add(rtcId);
              const eventListener = EventManager.getEventEmitter(rtcId);
              eventListener.on(Events.CallDataReceived, async () => {
                console.log("BgBlur: CallDataReceived event - applying custom bg settings");
                applyCustomBgSettings(rtcId);
                resetMedia();
              });
              eventListener.on(Events.VideoCallEnded, () => {
                currentBlurByRoom.get(rtcId)?.cleanUp();
                currentBlurByRoom.delete(rtcId);
                bgRegisteredRtcIds.delete(rtcId);
              });
            }
          }

          // Tear down THIS room's previous pipeline before building a new one (or before returning the raw
          // stream when blur is off): stops its render loop, inference worker, GL resources, and the old
          // raw camera/mic tracks. Scoped by roomKey so a whisper room never tears down the main call.
          currentBlurByRoom.get(roomKey)?.cleanUp();
          currentBlurByRoom.delete(roomKey);

          // On preview, background is OFF unless the agent opted in (avoids a 2nd pipeline tanking the PC);
          // during a call it follows the normal config-driven state.
          const active = isPreview ? useDeviceState().previewBackgroundEnabled : rawBgBlurState.backgroundActive.value;
          // Never build a pipeline for a stream with no camera track (e.g. an audio-only whisper room):
          // there's nothing to segment, and it would spin up a wasted 2nd pipeline.
          const hasVideo = (mediaOrj?.getVideoTracks?.() ?? []).length > 0;
          if (!active || !hasVideo) {
            return mediaOrj;
          }

          const blur = await bgBlur(this.getBgBlurParams());
          const processor = await blur.processVideo(await blur.createSourcePlayback(mediaOrj));
          currentBlurByRoom.set(roomKey, processor);
          return processor.localStream;
        },
        condition: CommonConditions.isAgent,
      }),
    );
  },
};

/*
runner: async ({media}) => {         
   
    },
*/
export default module;
